/* eslint-disable prefer-rest-params */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { inject, Injectable } from '@angular/core';
import { ReplaySubject, Subject } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { JWT, User } from '@core/api';
import { APP_CONFIG } from '@core/config';

interface IntercomParams {
  name: string;
  data: any[];
}

@Injectable({ providedIn: 'root' })
export class IntercomService {
  private booted = false;

  private intercom$: ReplaySubject<void> = new ReplaySubject(1);
  private request$: Subject<IntercomParams> = new Subject();

  private readonly config = inject(APP_CONFIG);

  constructor() {
    const { id, delay = 200 } = this.config.intercom;

    if (id) {
      this.install(id, delay).subscribe(() => this.intercom$.next());
    }

    this.request$.pipe(switchMap((params) => this.intercom$.pipe(map(() => params)))).subscribe(({ name, data }) => {
      (window as any).Intercom(name, ...data);
    });
  }

  public boot(user: User, jwt?: JWT) {
    if (!this.booted) {
      this.booted = true;
      this.request$.next({ name: 'boot', data: [this.getIntercomUser(user, jwt?.token)] });
    }
  }

  public trackEvent(eventName: string, metadata?: any) {
    this.request$.next({ name: 'trackEvent', data: [eventName, metadata] });
  }

  public shutdown() {
    if (this.booted) {
      this.booted = false;
      this.request$.next({ name: 'shutdown', data: [] });
    }
  }

  public show() {
    if (this.booted) {
      this.request$.next({ name: 'show', data: [] });
    }
  }

  public showNewMessage(message?: string) {
    if (this.booted) {
      this.request$.next({ name: 'showNewMessage', data: [message] });
    }
  }

  private getIntercomUser(user: User, token?: string) {
    if (user) {
      return {
        app_id: this.config.intercom.id,
        intercom_user_jwt: token,
      };
    } else {
      return null;
    }
  }

  private install(id: string, delay: number) {
    const installer$: Subject<void> = new Subject();

    (function () {
      const w = window as any;
      w.intercomSettings = {
        horizontal_padding: 20,
        vertical_padding: 20,
      };
      const ic = w.Intercom;
      if (typeof ic === 'function') {
        ic('reattach_activator');
        ic('update', w.intercomSettings);
      } else {
        const d = document;
        const i: any = function () {
          i.c(arguments);
        };
        i.q = [];
        i.c = function (args: any) {
          i.q.push(args);
        };
        w.Intercom = i;
        const l = function () {
          const s = d.createElement('script');
          s.type = 'text/javascript';
          s.async = true;
          s.src = 'https://widget.intercom.io/widget/' + id;
          s.onload = () => {
            setTimeout(() => {
              installer$.next();
              installer$.complete();
            }, delay);
          };
          const x = d.getElementsByTagName('script')[0];
          x.parentNode?.insertBefore(s, x);
        };
        if (document.readyState === 'complete') {
          l();
        } else if (w.attachEvent) {
          w.attachEvent('onload', l);
        } else {
          w.addEventListener('load', l, false);
        }
      }
    })();

    return installer$;
  }
}
